import { ArrowForwardIcon, CheckCircleIcon } from '@chakra-ui/icons'
import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { pipe, prop, sortBy } from 'ramda'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { SalesGroup } from '~shared-components/api/sales-groups'
import { Input } from '~shared-components/components'
import { ChangeSalesOrgModal } from '~shared-components/components/Modal/ChangeSalesOrgModal'
import { useRedirect } from '~shared-components/hooks/useRedirect'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { IconChevronDown } from '~shared-components/icons/brand/IconChevronDown'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import { Page } from '~shared-components/utils/enums'

import { LayoutPadding } from '../../theme'

interface SalesOrgSelectorProps {
  onChange?: (group: SalesGroup) => void
  salesGroups: SalesGroup[] | undefined
}

const getActiveSalesGroupName = (salesGroup: SalesGroup | undefined) =>
  salesGroup ? `${salesGroup.name} (${salesGroup.salesOrganization})` : ''

const optionsPipeline = pipe(sortBy(prop('salesOrganization')))

const CLEAR_VALUE = ''
const SHOW_SEARCH_INPUT_AFTER = 10
const pageRedirects = [
  {
    from: Page.OrderDetails,
    to: Page.Orders,
  },
  {
    from: Page.QuoteDetails,
    to: Page.Quotes,
  },
  {
    from: Page.ProductDetails,
    to: Page.Products,
  },
]

const sxActiveOption = {
  background: 'neutral.10',
  borderLeft: '6px solid',
  borderLeftColor: 'deepPurple',
  color: 'deepPurple',
  fontWeight: 'bold',
  paddingLeft: '18px',
}

const sxInactiveOption = {
  background: 'white',
  color: 'neutral.70',
  paddingLeft: '24px',
}

export const SalesOrgSelector = ({
  onChange,
  salesGroups,
}: SalesOrgSelectorProps) => {
  const t = useTranslations()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [
    activeSalesOrganisationName,
    setActiveSalesOrganisationName,
  ] = useState<string>('')
  const salesGroupRef = useRef<SalesGroup | undefined>()
  const [options, setOptions] = useState<SalesGroup[] | undefined>()
  const selectedSalesGroup = useSelector(LoginSelector.selectedSalesGroup)

  const pageType = useSelector(LocationSelector.pageType)
  const {
    isOpen: isModalOpen,
    onClose: modalClose,
    onOpen: modalOpen,
  } = useDisclosure()
  const redirect = useRedirect()

  const onChangeActiveSalesOrg = (salesGroup: SalesGroup) => {
    onChange?.(salesGroup)
    setActiveSalesOrganisationName(getActiveSalesGroupName(salesGroup))
  }

  const onChangeActiveSalesOrgModal = (salesGroup: SalesGroup) => {
    salesGroupRef.current = salesGroup
    modalOpen()
  }

  const handleModalConfirm = () => {
    onChangeActiveSalesOrg(salesGroupRef.current!)
    const isRedirecting = pageRedirects.find(r => pageType === r.from)
    if (isRedirecting) {
      redirect(isRedirecting.to)
    }
    modalClose()
  }

  const isActiveItem = (group: SalesGroup) =>
    selectedSalesGroup?.id === group.id

  const dropdownOptions: SalesGroup[] | undefined = useMemo(
    () => (salesGroups ? optionsPipeline(salesGroups) : undefined),
    [salesGroups],
  )

  const onSearch = (value: string) => {
    const filteredOptions = dropdownOptions?.filter(salesGroup =>
      [salesGroup.salesOrganization, salesGroup.name].some(entity =>
        entity.toLowerCase().includes(value),
      ),
    )
    setOptions(filteredOptions)
  }

  const clearInput = () => {
    // this is due to the animation for hiding the popup
    setTimeout(() => {
      if (inputRef && inputRef.current) {
        inputRef.current.value = CLEAR_VALUE
      }
      onSearch(CLEAR_VALUE)
    }, 100)
  }

  useEffect(() => {
    if (dropdownOptions) {
      const salesGroup =
        dropdownOptions?.find(o => o.id === selectedSalesGroup?.id) ||
        dropdownOptions?.[0]
      if (salesGroup) {
        setActiveSalesOrganisationName(getActiveSalesGroupName(salesGroup))
        clearInput()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOptions, selectedSalesGroup])

  if (!(salesGroups && salesGroups.length >= 2)) return null

  const hasSearch = (dropdownOptions?.length ?? 0) > SHOW_SEARCH_INPUT_AFTER

  return (
    <LayoutPadding
      align="flex-end"
      bg="neutral.10"
      height={10}
      justify="center"
      maxHeight={10}
      mb={[2, 0]}
      px={[2, 12, 24]}
      py={[2, 5]}
    >
      <Flex zIndex={1000}>
        <Popover matchWidth placement="bottom-end">
          {({ onClose }) => (
            <>
              <PopoverTrigger>
                <Text
                  color="neutral.80"
                  cursor="pointer"
                  fontSize={['12px', 'md']}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {activeSalesOrganisationName}{' '}
                  <IconChevronDown boxSize="16px" color="neutral.50" />
                </Text>
              </PopoverTrigger>
              <PopoverContent
                _focus={{
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                }}
                bg="neutral.10"
                border="1px solid"
                borderColor="neutral.20"
                borderRadius="4px"
                maxHeight={['100vh', 'lg']}
                maxWidth={['100vw', 'md', 'lg']}
                minHeight={['100vh', 'inherit']}
                minWidth={['100vw', 'md', 'lg']}
                onBlur={clearInput}
                overflowY="scroll"
              >
                <PopoverCloseButton
                  display={['block', 'none']}
                  right="1px"
                  top="-4px"
                />
                <PopoverBody p={6}>
                  {hasSearch && (
                    <Box mb={3}>
                      <Input
                        ref={inputRef}
                        showClearButton
                        onValueChange={onSearch}
                        placeholder={t('filter.search-salesorg.placeholder')}
                        type="search"
                      />
                    </Box>
                  )}
                  <VStack spacing={4}>
                    {options?.map((salesGroup: SalesGroup) => {
                      const isActiveOption = isActiveItem(salesGroup)
                      return (
                        <Flex
                          key={salesGroup.id}
                          align="center"
                          cursor="pointer"
                          justify="space-between"
                          onClick={() => {
                            onClose()
                            onChangeActiveSalesOrgModal(salesGroup)
                          }}
                          px={2}
                          py={3}
                          sx={
                            isActiveOption ? sxActiveOption : sxInactiveOption
                          }
                          w="full"
                        >
                          <Text>{getActiveSalesGroupName(salesGroup)}</Text>
                          <Flex ml={2}>
                            {isActiveOption ? (
                              <CheckCircleIcon color="deepPurple" w={4} />
                            ) : (
                              <ArrowForwardIcon color="neutral.50" w={4} />
                            )}
                          </Flex>
                        </Flex>
                      )
                    })}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </>
          )}
        </Popover>
      </Flex>
      <ChangeSalesOrgModal
        isOpen={isModalOpen}
        onClose={modalClose}
        onConfirm={handleModalConfirm}
      />
    </LayoutPadding>
  )
}
